import { URLHandler } from "src/services/URLHandler";
import { isSupplierManagedBy } from "./IngredientDetails/ActivitiesData/isSupplierManagedBy";

type SupplierLink = {
    supplier: { id: number };
    managedBy?: { id: number };
};

type AnswerableSupplierOptions = {
    selectedCompanyId: number | undefined;
    isSupplierUser: boolean;
    suppliers: SupplierLink[] | undefined;
};

export const getAnswerableSupplierId = (options: AnswerableSupplierOptions): number | null => {
    const { selectedCompanyId, isSupplierUser, suppliers = [] } = options;

    if (selectedCompanyId === undefined) {
        return null;
    }

    if (isSupplierUser || suppliers.some((link) => link.supplier.id === selectedCompanyId)) {
        return selectedCompanyId;
    }

    return null;
};

type ReadableSuppliersOptions = AnswerableSupplierOptions & {
    ingredientOwnerId?: number;
};

export const getReadableSupplierIds = (options: ReadableSuppliersOptions): number[] => {
    const { selectedCompanyId, ingredientOwnerId, suppliers = [] } = options;

    if (selectedCompanyId === undefined) {
        return [];
    }

    const answerableSupplierId = getAnswerableSupplierId(options);
    const isOwner = ingredientOwnerId === selectedCompanyId;
    const linkedSupplierIds = suppliers
        .filter((link) => isOwner || isSupplierManagedBy(link, selectedCompanyId))
        .map((link) => link.supplier.id);
    const supplierIds =
        answerableSupplierId === null ? linkedSupplierIds : [answerableSupplierId, ...linkedSupplierIds];

    return [...new Set(supplierIds)];
};

type QuestionnaireSupplierOptions = ReadableSuppliersOptions & {
    preferredSupplierId?: number | null;
};

export const getQuestionnaireSupplierId = (options: QuestionnaireSupplierOptions): number | null => {
    const { preferredSupplierId } = options;
    const readableSupplierIds = getReadableSupplierIds(options);

    if (typeof preferredSupplierId === "number" && readableSupplierIds.includes(preferredSupplierId)) {
        return preferredSupplierId;
    }

    return getAnswerableSupplierId(options) ?? readableSupplierIds[0] ?? null;
};

type ReadableSupplierCompany = { id: number };

export const selectReadableSupplierCompanies = <Company extends ReadableSupplierCompany>(
    suppliers: { supplier: Company }[] | undefined,
    readableSupplierIds: number[],
): Company[] => {
    const companies = new Map<number, Company>();

    (suppliers ?? []).forEach(({ supplier }) => {
        if (readableSupplierIds.includes(supplier.id) && !companies.has(supplier.id)) {
            companies.set(supplier.id, supplier);
        }
    });

    return [...companies.values()].sort(
        (a, b) => readableSupplierIds.indexOf(a.id) - readableSupplierIds.indexOf(b.id),
    );
};

type EntryUrlOptions = {
    companyId: number;
    ingredientId: number;
    questionnaireSupplierId: number | null;
};

export const getIngredientQuestionnaireEntryUrl = (options: EntryUrlOptions): string => {
    const { companyId, ingredientId, questionnaireSupplierId } = options;

    return questionnaireSupplierId === null ?
            URLHandler.ingredient.scores(companyId, ingredientId)
        :   URLHandler.ingredient.ssq(companyId, ingredientId, questionnaireSupplierId);
};
